<template>
    <tr class="domain-row" @click="goToSettings">
        <!-- DOMAIN -->
        <td class="px-6 py-4 min-w-0">
            <div class="flex items-center gap-2 min-w-0">
                <div
                    v-if="domain.active"
                    class="h-2 w-2 rounded-full bg-green-500"
                ></div>
                <div v-else class="h-2 w-2 rounded-full bg-gray-400"></div>
                <span
                    class="font-medium text-blue-600 transition-colors duration-150 hover:text-blue-800 min-w-0 truncate"
                    >{{ domain.domain }}</span
                >
            </div>
        </td>

        <!-- STATUS -->
        <td class="px-6 py-4 whitespace-nowrap min-w-0">
            <span
                :class="
                    domain.active
                        ? 'policy-badge badge-active'
                        : 'policy-badge badge-inactive'
                "
            >
                {{ domain.active ? "Active" : "Inactive" }}
            </span>
        </td>

        <!-- CREATED -->
        <td class="px-6 py-4 whitespace-nowrap min-w-0">
            <span class="cell-text">
                {{
                    format(new Date(domain.created_at as string), "d. L. yyyy")
                }}
            </span>
        </td>

        <!-- DMARC POLICY -->
        <td class="px-6 py-4 min-w-0">
            <span :class="policyBadgeClass">
                {{ policyLabel }}
            </span>
        </td>

        <!-- ACTIONS -->
        <td class="px-3 py-4 whitespace-nowrap text-center" @click.stop>
            <button
                ref="kebabBtn"
                class="kebab-btn"
                aria-haspopup="menu"
                :aria-expanded="menuOpen ? 'true' : 'false'"
                aria-label="Open actions"
                @click="toggleMenu"
            >
                <span class="kebab-dots" aria-hidden="true"></span>
            </button>

            <Teleport to="body">
                <transition name="fade">
                    <div
                        v-if="menuOpen"
                        ref="menu"
                        class="menu-popover"
                        :style="menuStyle"
                        role="menu"
                        @keydown.esc.stop.prevent="closeMenu"
                    >
                        <button
                            class="menu-item"
                            role="menuitem"
                            @click="goToSettings"
                        >
                            {{ $t("vue.settings") }}
                        </button>
                        <button
                            class="menu-item"
                            role="menuitem"
                            @click="goToReports"
                        >
                            {{ $t("vue.see_reports") }}
                        </button>
                        <button
                            class="menu-item danger"
                            role="menuitem"
                            @click="onDelete"
                        >
                            {{ $t("vue.delete_domain") }}
                        </button>
                    </div>
                </transition>
            </Teleport>
        </td>
    </tr>
</template>

<script lang="ts" setup>
import type { PropType } from "vue";
import { onMounted, onBeforeUnmount, ref, computed, nextTick } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { format } from "date-fns";
import type IDomain from "@/types/Domains/IDomain";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { useTranslations } from "@/composables/useTranslations";

const { t } = useTranslations();

const emit = defineEmits(["remove", "validate", "refreshDomains"]);

const props = defineProps({
    domain: { type: Object as PropType<IDomain>, required: true },
    validationLoading: { type: Boolean, default: false },
    dnsChecking: { type: Boolean, default: false },
});

const page = usePage<IRhizomPageProps>();
const globalProps = computed(() => page.props);

const menuOpen = ref(false);
const kebabBtn = ref<HTMLElement | null>(null);
const menu = ref<HTMLElement | null>(null);

const policyLabel = computed(() => {
    if (!props.domain.dmarcRecord) return "None";
    const p = (props.domain.dmarcPolicy ?? "none").toLowerCase();
    if (p === "reject") return "Reject";
    if (p === "quarantine") return "Quarantine";
    if (p === "none") return "None";
    return "Unknown";
});

const policyBadgeClass = computed(() => {
    const base = "policy-badge";
    if (!props.domain.dmarcRecord) return `${base} policy-none`;

    const p = (props.domain.dmarcPolicy ?? "none").toLowerCase();
    if (p === "reject") return `${base} policy-reject`;
    if (p === "quarantine") return `${base} policy-quarantine`;
    if (p === "none") return `${base} policy-none`;
    return `${base} policy-none`;
});

const goToReports = () => {
    router.get(route("reports.show", { domain: props.domain.domain }));
};

const goToSettings = () => {
    const url =
        route("reports.show", { domain: props.domain.domain }) + "/settings";
    router.get(url);
};

const onDelete = () => {
    closeMenu();
    emit("remove", props.domain.domain);
};

const menuPos = ref({ top: 0, left: 0 });

const updateMenuPosition = () => {
    const btn = kebabBtn.value;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const padding = 8;

    const menuW = menu.value?.offsetWidth ?? 176;
    const menuH = menu.value?.offsetHeight ?? 0;

    // default: pod buttonem, zarovnat doprava
    let top = rect.bottom + 8;
    let left = rect.right - menuW;

    // clamp horizontálně
    left = Math.min(
        Math.max(left, padding),
        window.innerWidth - menuW - padding,
    );

    // pokud by to teklo dolů mimo viewport, dej to nad button
    if (menuH && top + menuH > window.innerHeight - padding) {
        top = Math.max(padding, rect.top - menuH - 8);
    }

    menuPos.value = { top, left };
};

const menuStyle = computed(() => ({
    position: "fixed",
    top: `${menuPos.value.top}px`,
    left: `${menuPos.value.left}px`,
    zIndex: "99999",
}));

const toggleMenu = async () => {
    menuOpen.value = !menuOpen.value;

    if (menuOpen.value) {
        await nextTick();
        requestAnimationFrame(() => updateMenuPosition());
    }
};

const closeMenu = () => {
    menuOpen.value = false;
};

const onClickOutside = (e: MouseEvent) => {
    if (!menuOpen.value) return;
    const target = e.target as Node;
    if (
        menu.value &&
        !menu.value.contains(target) &&
        kebabBtn.value &&
        !kebabBtn.value.contains(target)
    ) {
        closeMenu();
    }
};

const onScrollOrResize = () => {
    if (!menuOpen.value) return;
    updateMenuPosition();
};

onMounted(() => {
    document.addEventListener("click", onClickOutside);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
});

onBeforeUnmount(() => {
    document.removeEventListener("click", onClickOutside);
    window.removeEventListener("resize", onScrollOrResize);
    window.removeEventListener("scroll", onScrollOrResize, true);
});
</script>

<style>
.domain-row {
    transition: background-color 0.12s ease;
    height: 49.75px;
}

.domain-row > td {
    height: 49.75px;
    padding-top: 6px;
    padding-bottom: 6px;
    vertical-align: middle;
}

.domain-row:hover td {
    background: #fafafa;
    cursor: pointer;
}

.cell-text {
    color: #374151;
}

.kebab-btn {
    display: inline-flex;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    border-radius: 8px;
}

.kebab-btn:hover {
    background: #f3f4f6;
}

.kebab-dots {
    width: 4px;
    height: 4px;
    border-radius: 999px;
    background: #6b7280;
    position: relative;
}
.kebab-dots::before,
.kebab-dots::after {
    content: "";
    position: absolute;
    left: 0;
    width: 4px;
    height: 4px;
    border-radius: 999px;
    background: #6b7280;
}
.kebab-dots::before {
    top: -7px;
}
.kebab-dots::after {
    top: 7px;
}

.menu-item {
    display: block;
    width: 100%;
    text-align: left;
    padding: 8px 12px;
    font-size: 14px;
    line-height: 20px;
    color: #374151;
}
.menu-item:hover {
    background: #f9fafb;
}
.menu-item.danger {
    color: #b91c1c;
}
.menu-item.danger:hover {
    background: #fef2f2;
}

.menu-popover {
    z-index: 99999;
    width: 176px;
    border: 1px solid #e5e7eb;
    background: #fff;
    border-radius: 8px;
    box-shadow:
        0 8px 24px rgba(16, 24, 40, 0.12),
        0 2px 8px rgba(16, 24, 40, 0.06);
    padding: 6px 0;
}
.menu-popover:hover,
.menu-item:hover {
    cursor: pointer;
}
</style>
